import { queryOptions, type QueryClient } from "@tanstack/react-query";
import type { WorkspaceJobRoleService } from "@/services/workspace-job-role.service";
import type { TJobRole } from "@/types/job-role";
import { FRESHNESS } from "./policies";
import { getQueryClient } from "./client";
import { scopeIfReady, scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The workspace's job roles (NG-2802):
 *
 * - `jobRoles.list`: every job role with its counts, the settings list and the
 *   filters' bank, `[slug, uid, 'job-roles', 'list']`. Needs `job_role.read`.
 * - `jobRoles.options`: the work-item pickers' choices (id, name, colour,
 *   status), readable by anyone who can create or edit work (NG-2099),
 *   `[slug, uid, 'job-roles', 'options']`.
 * - `jobRoles.members`: one role's eligible members, each flagged `assigned`,
 *   `[slug, uid, 'job-roles', 'members', jobRoleId]`.
 * - `jobRoles.mine`: the signed-in member's own membership (their job role
 *   and workspace role), `[slug, uid, 'job-roles', 'mine']`.
 *
 * The Smart Assignment recommendations are a project read (R13,
 * `queries/project-resources.ts`).
 *
 * Reference data: `WorkspaceJobRolesStore` cached them for the whole session;
 * as queries they are reused for 5 minutes, re-read by this tab's writes, and
 * by another tab's through the `job-roles:bank` hint. Every read is a plain
 * idempotent GET, so each declares `"safe-read"`.
 *
 * @writers `useJobRoleWriters` (`app/hooks/use-job-roles.ts`): create, edit,
 * activate or deactivate, delete, set a role's members, set my own job role,
 * and Smart Assign. Each settles what it can with the answer and invalidates
 * everything under `'job-roles'` once the write is confirmed.
 */

/** The `jobRoles.list` key. */
export function jobRolesKey(scope: QueryScope) {
  return scopedKey(scope, "job-roles", "list");
}

/** The `jobRoles.options` key. */
export function jobRoleOptionsKey(scope: QueryScope) {
  return scopedKey(scope, "job-roles", "options");
}

/** The `jobRoles.members` key for one role. */
export function jobRoleMembersKey(scope: QueryScope, jobRoleId: string) {
  return scopedKey(scope, "job-roles", "members", jobRoleId);
}

/** The `jobRoles.mine` key. */
export function myJobRoleKey(scope: QueryScope) {
  return scopedKey(scope, "job-roles", "mine");
}

/** `jobRoles.list`: the workspace's job roles. A nullish answer reads as none. */
export function jobRolesQuery(service: Pick<WorkspaceJobRoleService, "list">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: jobRolesKey(scope),
    queryFn: scopedRead(
      async (request) => {
        const roles = await service.list(scope.tenant, request);
        return Array.isArray(roles) ? roles : [];
      },
      { retry: "safe-read" },
    ),
  });
}

/** `jobRoles.options`: the pickers' choices. A nullish answer reads as none. */
export function jobRoleOptionsQuery(
  service: Pick<WorkspaceJobRoleService, "listOptions">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: jobRoleOptionsKey(scope),
    queryFn: scopedRead(
      async (request) => {
        const options = await service.listOptions(scope.tenant, request);
        return Array.isArray(options) ? options : [];
      },
      { retry: "safe-read" },
    ),
  });
}

/** `jobRoles.members`: one role's eligible members, each flagged `assigned`. */
export function jobRoleMembersQuery(
  service: Pick<WorkspaceJobRoleService, "members">,
  scope: QueryScope,
  jobRoleId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: jobRoleMembersKey(scope, jobRoleId),
    queryFn: scopedRead((request) => service.members(scope.tenant, jobRoleId, request), {
      retry: "safe-read",
    }),
  });
}

/** `jobRoles.mine`: the signed-in member's own membership. */
export function myJobRoleQuery(
  service: Pick<WorkspaceJobRoleService, "getMyMembership">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: myJobRoleKey(scope),
    queryFn: scopedRead((request) => service.getMyMembership(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** After a confirmed job-role write: re-read every cached job-role read in the workspace. */
export function invalidateJobRoles(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: scopedKey(scope, "job-roles") });
}

/**
 * Put a write's answer into the cached settings list at once, before the
 * re-read: a created role is added, an edited one replaced, a deleted one
 * dropped. A list not yet read is left alone.
 */
export function patchJobRoles(
  client: QueryClient,
  scope: QueryScope,
  change:
    | { written: TJobRole }
    | { patched: string; patch: Partial<TJobRole> }
    | { deletedId: string },
): void {
  client.setQueryData<TJobRole[]>(jobRolesKey(scope), (roles) => {
    if (!roles) return roles;
    if ("deletedId" in change) return roles.filter((r) => r.id !== change.deletedId);
    if ("patched" in change) {
      return roles.map((r) => (r.id === change.patched ? { ...r, ...change.patch } : r));
    }
    const { written } = change;
    return roles.some((r) => r.id === written.id)
      ? roles.map((r) => (r.id === written.id ? written : r))
      : [...roles, written];
  });
}

/**
 * Warm `jobRoles.list` from a route loader, so the page's `useJobRoles`
 * answers from the cache on arrival. Skips before the session resolves, when
 * the page's own read takes over.
 */
export async function prefetchJobRoles(
  service: Pick<WorkspaceJobRoleService, "list">,
  slug: string,
): Promise<void> {
  const scope = scopeIfReady(slug, "job roles");
  if (!scope) return;
  await getQueryClient().prefetchQuery(jobRolesQuery(service, scope));
}
